import { Directive, ElementRef, inject } from '@angular/core';

/** Elemento focável de cada card: o link do título (cards que navegam) ou o próprio card (cards de dialog). */
const CARD_SELECTOR = '.card-focus-target';
const COLUMN_SELECTOR = '.cards-container';
const HANDLED_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

/**
 * Navegação por setas entre os cards do quadro.
 *
 * Com o foco num card: ↑/↓ trocam de card na coluna, ←/→ vão para o card na mesma
 * posição da coluna vizinha (pulando colunas recolhidas ou vazias), Home/End vão ao
 * primeiro e ao último da coluna. Tab continua funcionando como antes; as setas são
 * o atalho para não passar por todos os botões internos de cada card.
 */
@Directive({
  selector: '[aionzKanbanKeyboardNav]',
  host: { '(keydown)': 'onKeydown($event)' }
})
export class KanbanKeyboardNavDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected onKeydown(event: KeyboardEvent): void {
    if (!HANDLED_KEYS.has(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;

    // Só quando o foco está no card em si, não num botão ou campo dentro dele.
    const current = event.target as HTMLElement | null;
    if (!current?.matches(CARD_SELECTOR)) return;

    const column = current.closest<HTMLElement>(COLUMN_SELECTOR);
    if (!column) return;

    const cards = this.cardsOf(column);
    const index = cards.indexOf(current);
    const next = this.resolveTarget(event.key, column, cards, index);

    if (!next) return;

    event.preventDefault();
    next.focus();
    next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private resolveTarget(
    key: string,
    column: HTMLElement,
    cards: HTMLElement[],
    index: number
  ): HTMLElement | undefined {
    switch (key) {
      case 'ArrowDown':
        return cards[index + 1];
      case 'ArrowUp':
        return cards[index - 1];
      case 'Home':
        return cards[0];
      case 'End':
        return cards[cards.length - 1];
      case 'ArrowRight':
        return this.sameRowInNeighbour(column, index, 1);
      case 'ArrowLeft':
        return this.sameRowInNeighbour(column, index, -1);
      default:
        return undefined;
    }
  }

  /** Card na mesma linha da próxima coluna com cards, na direção dada. */
  private sameRowInNeighbour(column: HTMLElement, index: number, direction: 1 | -1): HTMLElement | undefined {
    const columns = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(COLUMN_SELECTOR));
    let i = columns.indexOf(column) + direction;

    while (i >= 0 && i < columns.length) {
      const cards = this.cardsOf(columns[i]);
      if (cards.length) return cards[Math.min(index, cards.length - 1)];
      i += direction;
    }

    return undefined;
  }

  private cardsOf(column: HTMLElement): HTMLElement[] {
    return Array.from(column.querySelectorAll<HTMLElement>(CARD_SELECTOR));
  }
}
